const IMetricsClient = require("./IMetricsClient.js");

const FLUSH_TIMEOUT_MS = 2000;

/**
 * Event properties allowed onto Sentry metrics as attributes.
 *
 * Deny-by-default: only low-cardinality, non-PII, event-moment dimensions that
 * cannot be reconstructed from a database query are kept. Everything else —
 * entity ids, descriptive fields derivable via those ids, names, tokens, urls,
 * and the acting user id — is dropped, because it is DB-derivable, sensitive,
 * or would explode metric cardinality. Slicing and attribution are done via DB
 * queries, not Sentry.
 */
const DEFAULT_ALLOWED_ATTRIBUTES = Object.freeze([
  "render_type", // design_render: export format (pdf/png/jpg/gif)
  "crop_and_bleed", // design_render: boolean
  "gif_speed", // design_render: gif-only speed setting
  "source", // add_page: which UI affordance (mobile panel vs button)
  "category_name", // category_view: browsed size/category (no persisted row)
  "page_name" // page_view: readable route label for page-view trends
]);

/**
 * Metrics client backed by Sentry's Trace Metrics API (`Sentry.metrics.*`).
 *
 * Each tracked event is recorded as a counter via `metrics.count()`, so events
 * land in Sentry's metrics product — NOT the error/issue stream. Allowlisted
 * event properties are attached as metric attributes for querying.
 * The Sentry SDK is injected so this client is provider-agnostic and testable —
 * pass `@sentry/react` on the frontend or `@sentry/node` on the backend.
 *
 * Trace Metrics are enabled by default in the Sentry SDK (v10+); no special
 * `Sentry.init()` flag is required.
 */
class SentryClient extends IMetricsClient {
  /**
   * @param {object} sentry The Sentry SDK (e.g. `@sentry/react` / `@sentry/node`).
   * @param {object} [options]
   * @param {string[]} [options.allowedAttributes] Override the attribute
   *   allowlist applied in `track`. Defaults to DEFAULT_ALLOWED_ATTRIBUTES.
   * @param {(error: Error) => void} [options.onError] See {@link IMetricsClient}.
   */
  constructor(
    sentry,
    { allowedAttributes = DEFAULT_ALLOWED_ATTRIBUTES, onError } = {}
  ) {
    super({ onError });
    this.sentry = sentry;
    this.allowedAttributes = new Set(allowedAttributes);
  }

  /**
   * Record an event as a Sentry counter. Event properties are filtered to the
   * attribute allowlist (DB-derivable ids/descriptors, sensitive values and the
   * acting user id are dropped). `userId` is accepted for interface parity but
   * deliberately not emitted — there is no per-user attribution in the metrics
   * product.
   */
  // eslint-disable-next-line no-unused-vars
  track(userId, eventName, properties = {}) {
    // Plain checks (no optional chaining) so the file parses on Node 12 — the
    // package is required by CommonJS services even when they use Segment.
    if (
      !this.sentry ||
      !this.sentry.metrics ||
      typeof this.sentry.metrics.count !== "function"
    )
      return;

    const attributes = {};
    const props = properties || {};
    for (const key of Object.keys(props)) {
      if (this.allowedAttributes.has(key)) attributes[key] = props[key];
    }

    this.safeInvoke(() =>
      this.sentry.metrics.count(eventName, 1, { attributes })
    );
  }

  /**
   * Associate the acting user with the Sentry scope. Only the user id is sent —
   * `traits` (email / name / team metadata, etc.) are deliberately NOT forwarded
   * so the migration never leaks PII into Sentry. The id is enough to correlate
   * a user with their events/errors; richer attributes live in the DB.
   */
  // eslint-disable-next-line no-unused-vars
  identify(userId, traits = {}) {
    if (!userId || !this.sentry || typeof this.sentry.setUser !== "function")
      return;
    this.safeInvoke(() => this.sentry.setUser({ id: userId }));
  }

  /**
   * Associate the acting user with a group on the Sentry scope. Only the group
   * and user identifiers are sent — `groupTraits` (team name, plan metadata,
   * etc.) are deliberately NOT forwarded, matching `identify()`, so the
   * migration never leaks PII into Sentry. Descriptive group attributes live in
   * the DB.
   */
  // eslint-disable-next-line no-unused-vars
  group(groupId, userId, groupTraits = {}) {
    if (!this.sentry || typeof this.sentry.setContext !== "function") return;
    this.safeInvoke(() =>
      this.sentry.setContext("group", { id: groupId, userId })
    );
  }

  async flush() {
    if (this.sentry && typeof this.sentry.flush === "function") {
      await this.safeInvoke(() => this.sentry.flush(FLUSH_TIMEOUT_MS));
    }
  }
}

module.exports = SentryClient;
module.exports.DEFAULT_ALLOWED_ATTRIBUTES = DEFAULT_ALLOWED_ATTRIBUTES;
